/**
 * 🤖 AUTO-GENERATED API SCHEMA FILE - DO NOT EDIT DIRECTLY!
 *
 * Generated from: user_profiles entity configuration
 * Generator: scripts/generate-api-clients/generate-api-clients.ts
 *
 * This file contains the API client for the user_profiles table with:
 * - Type-safe CRUD operations
 * - Field-level security enforcement
 * - Search functionality with multi-term search and global config integration
 * - Validation using generated schemas
 *
 * @description
 * This client provides a complete interface for interacting with the user_profiles API.
 * All operations respect the field permissions defined in the entity configuration.
 *
 * Public fields: [id, email, full_name, avatar_url, phone_number, settings, created_at, updated_at, organization_id, auth_user_id, builder_id, trig_role_name, session_valid_after, is_active, deactivated_at, deactivated_by]
 * Searchable fields: []
 * Filterable fields: [id, organization_id, auth_user_id, builder_id, is_active]
 */

import { httpClient } from '../http-client.js';
import { globalSearchConfig } from '../../../config/search/global-search-config.js';
import { matchesAllTerms } from '../../../config/search/search-utils.js';
import type {
  UserProfilesRow,
  UserProfilesInsert,
  UserProfilesUpdate
} from '../generated_schemas/generated-user_profiles-schema.js';
import { UserProfilesQueryOptions } from '../generated_schemas/generated-user_profiles-schema.js';
export type { UserProfilesQueryOptions };

// Internal response type with non-null data (not exported to avoid conflicts with schema's Response type)
interface UserProfilesClientResponse {
  data: UserProfilesRow[];
  count?: number;
  success: boolean;
}

/**
 * API Client for user_profiles table operations
 *
 * @description Provides type-safe access to user_profiles API endpoints with
 * field-level security enforcement and comprehensive error handling.
 */
export class UserProfilesClient {
  private readonly basePath = '/user-profiles';

  /**
   * Get all user_profiles records with optional filtering and pagination
   *
   * @param options - Query options for filtering, sorting, and pagination
   * @returns Promise resolving to array of user_profiles records
   */
  async getAll(options?: UserProfilesQueryOptions): Promise<UserProfilesRow[]> {
    const params = new URLSearchParams();
    
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.sortOrder) params.set('sortOrder', options.sortOrder);
    if (options?.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;
    
    const response = await httpClient.get<UserProfilesClientResponse>(url);
    return response.data;
  }

  /**
   * Get a single user_profiles record by ID
   *
   * @param id - Unique identifier for the record
   * @returns Promise resolving to the user_profiles record
   */
  async getById(id: string): Promise<UserProfilesRow> {
    const response = await httpClient.get<{
      data: UserProfilesRow;
      success: boolean;
    }>(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Create a new user_profiles record
   *
   * @param data - Data for creating the new record
   * @returns Promise resolving to the created user_profiles record
   */
  async create(data: UserProfilesInsert): Promise<UserProfilesRow> {
    const response = await httpClient.post<{
      data: UserProfilesRow;
      success: boolean;
    }>(this.basePath, data);
    return response.data;
  }

  /**
   * Update an existing user_profiles record
   *
   * @param id - Unique identifier for the record to update
   * @param data - Updated data for the record
   * @returns Promise resolving to the updated user_profiles record
   */
  async update(id: string, data: UserProfilesUpdate): Promise<UserProfilesRow> {
    const response = await httpClient.put<{
      data: UserProfilesRow;
      success: boolean;
    }>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Delete a user_profiles record
   *
   * @param id - Unique identifier for the record to delete
   * @returns Promise resolving when deletion is complete
   */
  async delete(id: string): Promise<void> {
    await httpClient.delete(`${this.basePath}/${id}`);
  }

  /**
   * Create multiple user_profiles records in a single request
   *
   * @param data - Array of data for creating new records
   * @returns Promise resolving to array of created user_profiles records
   */
  async createMany(data: UserProfilesInsert[]): Promise<UserProfilesRow[]> {
    const response = await httpClient.post<{
      data: UserProfilesRow[];
      success: boolean;
    }>(`${this.basePath}/batch`, { items: data });
    return response.data;
  }

  /**
   * Delete multiple user_profiles records
   *
   * @param ids - Array of unique identifiers for records to delete
   * @returns Promise resolving when deletion is complete
   */
  async deleteMany(ids: string[]): Promise<void> {
    await httpClient.delete(`${this.basePath}/batch`, { ids });
  }

  /**
   * Search user_profiles with text query
   * ⚠️  SEARCH DISABLED: No fields marked as searchable in entity config
   */
  async search(
    query: string,
    columns?: string[],
    options?: UserProfilesQueryOptions
  ): Promise<UserProfilesClientResponse> {
    throw new Error('Search operations are not available for user_profiles - no searchable fields configured');
  }

  /**
   * Get count of user_profiles records matching optional filters
   *
   * @param filters - Optional filters to apply when counting
   * @returns Promise resolving to the count of matching records
   */
  async count(filters?: Record<string, any>): Promise<number> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}/count?${queryString}` : `${this.basePath}/count`;
    
    const response = await httpClient.get<{ data: number; success: boolean }>(url);
    return response.data;
  }

  /**
   * Check if a user_profiles record exists by ID
   *
   * @param id - Unique identifier to check
   * @returns Promise resolving to boolean indicating existence
   */
  async exists(id: string): Promise<boolean> {
    try {
      await this.getById(id);
      return true;
    } catch (error) {
      // Assuming 404 means record doesn't exist
      return false;
    }
  }

  /**
   * Get field configuration metadata for this entity
   *
   * @returns Object containing field permission and configuration data
   */
  getFieldConfig() {
    return {
      tableName: 'user_profiles',
      publicFields: ['id', 'email', 'full_name', 'avatar_url', 'phone_number', 'settings', 'created_at', 'updated_at', 'organization_id', 'auth_user_id', 'builder_id', 'trig_role_name', 'session_valid_after', 'is_active', 'deactivated_at', 'deactivated_by'],
      searchableFields: [],
      filterableFields: ['id', 'organization_id', 'auth_user_id', 'builder_id', 'is_active'],
      sortableFields: ['created_at', 'updated_at'],
      readonlyFields: ['id', 'created_at', 'updated_at', 'trig_role_name', 'deactivated_at', 'deactivated_by'],
      requiredFields: ['full_name'],
    };
  }
}

// Export singleton instance for convenience
export const userProfilesClient = new UserProfilesClient();
